import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { COPY } from "../shared/copy";
import { SwipeCard } from "../components/SwipeCard";
import type { OptionKey } from "../shared/types";
import { cleanProse } from "../shared/sanitize";
import { reviewOrder, sendToBack, progressLabel } from "../shared/review-queue";

// Candidate review. Shows the EXACT future game material, one card at a time.
// The subject approves, reframes (same source fact), edits, or rejects. Nothing
// is gameable until PLAY IT.
export function Candidates() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState<any[]>([]);
  const [approvedCount, setApprovedCount] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<any>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // The screen used to render "Nothing left to review" and "You've got 0
  // questions ready" for a beat before the real rows arrived, which reads as
  // "the AI produced nothing" at exactly the wrong moment.
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  // Reframed and edited cards go to the back of the pile (see review-queue).
  const [sentBack, setSentBack] = useState<string[]>([]);
  // Bumped when a swipe fails, so the card that flew off comes back instead of
  // staying invisible in its flung state.
  const [retry, setRetry] = useState(0);
  // Play it and Don't use send the card away exactly like a swipe does, so the
  // same green tick or grey "Not used" confirms it either way.
  const [commit, setCommit] = useState<1 | -1 | null>(null);
  // The first card of a visit leans once, so the deck reads as draggable.
  const [nudged, setNudged] = useState(() => {
    try { return sessionStorage.getItem("review_nudged") === "1"; } catch { return false; }
  });

  async function load() {
    if (!user) return;
    // Scoped to this league. Without it someone in two leagues sees both leagues'
    // pending questions in one undifferentiated list with no way to tell which is
    // which, and approves things for a league they were not thinking about.
    const { data: rows } = await supabase
      .from("question_candidates")
      .select("*")
      .eq("owner_user_id", user.id)
      .eq("league_id", leagueId)
      .order("created_at", { ascending: true });
    setPending((rows ?? []).filter((r) => r.status === "pending"));
    setApprovedCount((rows ?? []).filter((r) => r.status === "approved").length);
    setLoading(false);
  }
  useEffect(() => { load(); }, [user, leagueId]);

  // Every action here used to swallow its outcome. A reframe the model could not
  // produce came back 200 with {status:"reframe_failed"}, which was discarded,
  // so the button reloaded the identical card and looked broken. A network
  // failure rejected an un-awaited promise and did nothing at all.
  async function act(candidateId: string, op: string, extra: any = {}) {
    setBusy(op);
    setNote("");
    // Play it and Don't use are final, so the next card shows straight away
    // rather than after the round trip. A failure puts the card back.
    const decided = op === "approve" || op === "reject";
    if (decided) setPending((p) => p.filter((x) => x.id !== candidateId));
    try {
      const r = await api<any>(`/api/candidates/action`, { body: { op, candidateId, ...extra } });
      if (r?.status === "reframe_failed") {
        // Nothing changed, so the card stays up where they can Edit or drop it.
        setNote("Couldn't find another way to ask that one. Try Edit, or Don't use it.");
      } else {
        if (op === "approve") setNote("Locked in. That one can show up in a drop now.");
        else if (op === "reject") setNote("Gone. Nobody sees that one, ever.");
        else {
          setNote(op === "reframe"
            ? "Reworded. It's at the back of the pile for another look."
            : "Saved. It's at the back of the pile for another look.");
          setSentBack((b) => sendToBack(b, candidateId));
        }
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setNote(e?.message ?? "That didn't go through. Try it again.");
      if (decided) { await load(); setRetry((n) => n + 1); }
    } finally {
      setBusy(null);
      setCommit(null);
    }
  }

  const queue = reviewOrder(pending, sentBack);
  const current = queue[0];
  // Spent as soon as a card is on screen: the hint is for the first one only.
  useEffect(() => {
    if (!current || nudged) return;
    const t = window.setTimeout(() => {
      setNudged(true);
      try { sessionStorage.setItem("review_nudged", "1"); } catch { /* private mode */ }
    }, 2400);
    return () => window.clearTimeout(t);
  }, [current, nudged]);
  const next = queue[1];

  const header = (
    <div className="header">
      <h1 className={current ? "review-title" : undefined}>{COPY.candidates.intro}</h1>
      <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Done</button>
    </div>
  );

  // Editing is a form, so it gets an ordinary scrolling page, not the deck.
  if (current && editing === current.id) {
    return (
      <div className="app">
        {header}
        <EditCard
          candidate={draft}
          onChange={setDraft}
          onCancel={() => setEditing(null)}
          onSave={() => act(current.id, "edit", { question: draft })}
        />
      </div>
    );
  }

  // One card at a time, sized to the space between the crew bar and the tabs.
  //
  // This used to be every pending question stacked down a scrolling page. Each
  // one was a sideways-swipe card, so a thumb on a card fought the page scroll
  // and the only place the page would scroll from was the header. Each card also
  // carried a full column of buttons, so the bottom ones fell off the screen.
  if (!loading && current) {
    return (
      <div className="app review-screen">
        {header}
        <div className="review-meta">
          <span className="muted" aria-live="polite">{note || COPY.candidates.readyCount(approvedCount)}</span>
          <span className="pill">{progressLabel(queue.length)}</span>
        </div>

        <div className="deck-stage">
          {next && <div className="card peek-card" aria-hidden="true" />}
          <SwipeCard
            key={`${current.id}:${current.updated_at ?? ""}:${retry}`}
            className="card candidate-card"
            decide
            hintLeft={COPY.candidates.dontUse}
            hintRight={COPY.candidates.playIt}
            doneLeft="Not used"
            doneRight="Played"
            commit={commit}
            nudge={!nudged}
            onSwipe={(dir) => {
              // Clear it in the same update that drops this card, or the next
              // card mounts with the commit still set and flies away on its own.
              setCommit(null);
              act(current.id, dir === 1 ? "approve" : "reject");
            }}
          >
            <CandidateBody c={current} />
          </SwipeCard>
        </div>

        {/* Outside the card, so they never move with it and never scroll away.
            The swipe is the fast path; these are how anyone who would rather tap
            gets through, and the only way to reframe or edit. */}
        <div className="review-actions">
          <button className="btn danger" disabled={!!busy} onClick={() => setCommit(-1)}>
            {busy === "reject" ? "…" : COPY.candidates.dontUse}
          </button>
          <button className="btn ghost" disabled={!!busy} onClick={() => act(current.id, "reframe")}>
            {busy === "reframe" ? "…" : COPY.candidates.reframe}
          </button>
          <button className="btn ghost" disabled={!!busy} onClick={() => { setDraft({ ...current }); setEditing(current.id); }}>
            {COPY.candidates.edit}
          </button>
          <button className="btn primary" disabled={!!busy} onClick={() => setCommit(1)}>
            {busy === "approve" ? "…" : COPY.candidates.playIt}
          </button>
        </div>
        <p className="review-hint">Or swipe: right to play it, left to leave it out.</p>
      </div>
    );
  }

  return (
    <div className="app">
      {header}
      <p className="muted">{COPY.candidates.readyCount(approvedCount)}</p>

      {note && (
        <div className="banner">
          <span>{note}</span>
          <button className="link" onClick={() => setNote("")}>dismiss</button>
        </div>
      )}

      {loading && <div className="loading">Loading your questions…</div>}

      {!loading && (
        <div className="card live">
          <span className="tag">{approvedCount > 0 ? "You're set" : "Nothing to review"}</span>
          {approvedCount > 0 ? (
            <>
              <p>
                You&apos;ve got <b>{approvedCount}</b> question{approvedCount === 1 ? "" : "s"} ready about you.
                They&apos;ll show up in a drop when the time comes, and you sit out your own.
              </p>
              <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>Back to the crew</button>
              <button className="link" onClick={() => navigate(`/league/${leagueId}/checkin`)}>Add another update</button>
            </>
          ) : (
            <>
              <p>Nothing is waiting on you right now. Give the crew an update and we&apos;ll build questions from it.</p>
              <button className="btn primary" onClick={() => navigate(`/league/${leagueId}/checkin`)}>Check in</button>
              <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Back to the crew</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// What the question will look like in a drop. Only this part scrolls, and only
// up and down, so a long question never pushes the buttons off the screen and a
// scroll never turns into a swipe.
function CandidateBody({ c }: { c: any }) {
  const [showReveal, setShowReveal] = useState(false);
  const letters: OptionKey[] = ["a", "b", "c", "d"];
  return (
    <div className="cand-scroll">
      <span className="tag">{COPY.candidates.labelQuestion}</span>
      <p className="cand-question">{cleanProse(c.question_text)}</p>
      <div className="cand-options">
        {letters.map((l) => {
          const right = c.correct_option === l;
          return (
            <div key={l} className={`cand-option${right ? " correct" : ""}`}>
              <span className="cand-key">{l.toUpperCase()}</span>
              <span className="cand-text">{cleanProse(c[`option_${l}`])}</span>
              {right && <span className="cand-right">Right answer</span>}
            </div>
          );
        })}
      </div>
      {/* Tucked away to keep the card short. It matters, but it is not what
          anyone decides on. */}
      <button
        className="cand-reveal-toggle"
        aria-expanded={showReveal}
        onClick={() => setShowReveal((v) => !v)}
      >
        <span className="tag">{COPY.candidates.labelReveal}</span>
        <span aria-hidden="true">{showReveal ? "▴" : "▾"}</span>
      </button>
      {showReveal && <p className="muted cand-reveal">{cleanProse(c.reveal_context)}</p>}
    </div>
  );
}

function EditCard({ candidate, onChange, onCancel, onSave }: { candidate: any; onChange: (c: any) => void; onCancel: () => void; onSave: () => void }) {
  const letters: OptionKey[] = ["a", "b", "c", "d"];
  const set = (k: string, v: string) => onChange({ ...candidate, [k]: v });
  return (
    <div className="card">
      <span className="tag">Edit question</span>
      <textarea className="input" value={candidate.question_text} onChange={(e) => set("question_text", e.target.value)} />
      {letters.map((l) => (
        <div className="row" key={l}>
          <input className="input" value={candidate[`option_${l}`]} onChange={(e) => set(`option_${l}`, e.target.value)} />
          <label className="pill">
            <input type="radio" name="correct" checked={candidate.correct_option === l} onChange={() => set("correct_option", l)} /> correct
          </label>
        </div>
      ))}
      <span className="tag">{COPY.candidates.labelReveal}</span>
      <textarea className="input" value={candidate.reveal_context} onChange={(e) => set("reveal_context", e.target.value)} />
      <div className="row">
        <button className="btn ghost" onClick={onCancel}>Cancel</button>
        <button className="btn primary" onClick={onSave}>Save & review</button>
      </div>
    </div>
  );
}
